/* eslint-disable @next/next/no-img-element */
import {
  AuthFill,
  Button,
  ClosestDealers,
  Comments,
  DropDown,
  Email,
  FindDealer,
  FirstName,
  LastName,
  MarketingConsent,
  Models,
  Tel,
  Title,
  VehicleRegNo,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  ERROR_MESSAGE,
  TYPE_OF_ENQUIRIES_LEXUS,
  TYPE_OF_ENQUIRIES_LEXUS_VALUES,
  TYPE_OF_ENQUIRIES_OLSO,
  TYPE_OF_ENQUIRIES_OLSO_VALUES,
  TYPE_OF_ENQUIRIES_OTSO,
  TYPE_OF_ENQUIRIES_OTSO_VALUES,
  TYPE_OF_ENQUIRIES_TOYOTA,
  TYPE_OF_ENQUIRIES_TOYOTA_VALUES,
} from '@utils/constants'
import {
  closeOverlay,
  getModels,
  getPageData,
  removeDuplicate,
} from '@utils/helpers'
import { formEvent } from '@utils/helpers/tracking'
import { ContactUsDealerSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

const Dealer = ({
  models,
  content,
  tracking,
  brand,
  dealer,
  model,
  servicing,
  query,
}) => {
  const campaign = useCampaign()
  const [submitResponse, setSubmitResponse] = useState('')
  const [dealers, setDealers] = useState([])
  const [dealersList, setDealersList] = useState([])
  const [isDealer, setIsDealer] = useState(false)
  const [isModelQuery, setIsModelQuery] = useState(false)
  const [isEnquiryQuery, setIsEnquiryQuery] = useState(false)
  const [searchTerm, setSerchTerm] = useState('')
  const [typeOfEnquiries, setTypeOfEnquiries] = useState([])
  const [typeOfEnquiriesValues, setTypeOfEnquiriesValues] = useState([])
  const [showReg, setShowReg] = useState(false)
  const [showModel, setShowModel] = useState(false)

  const initialValues = {
    type: 'CU',
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    tel: '',
    type_dropdown: '',
    type_of_enquiry: '',
    enquiry_description: '',
    subject: '',
    pvt_company_car: '',
    reg_num: '',
    model: '',
    consent: {
      user: false,
    },
    is_dealer: query?.source === 'dealer' || query?.source === 'centre',
    dealer: '',
    dealer_name: '',
    campaign_response_code: '',
    campaign_code: '',
    response_code: '',
    crm_response_source: 'CentreWebsite',
    source: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(ContactUsDealerSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    if (servicing) {
      if (brand === 'lexus') {
        setTypeOfEnquiries(TYPE_OF_ENQUIRIES_OLSO)
        setTypeOfEnquiriesValues(TYPE_OF_ENQUIRIES_OLSO_VALUES)
      } else {
        setTypeOfEnquiries(TYPE_OF_ENQUIRIES_OTSO)
        setTypeOfEnquiriesValues(TYPE_OF_ENQUIRIES_OTSO_VALUES)
      }
    } else {
      setTypeOfEnquiries(
        brand === 'lexus' ? TYPE_OF_ENQUIRIES_LEXUS : TYPE_OF_ENQUIRIES_TOYOTA,
      )
      setTypeOfEnquiriesValues(
        brand === 'lexus'
          ? TYPE_OF_ENQUIRIES_LEXUS_VALUES
          : TYPE_OF_ENQUIRIES_TOYOTA_VALUES,
      )
    }
  }, [])

  useEffect(() => {
    if (query?.model) {
      setShowModel(false)
      setIsModelQuery(true)
      methods.setValue('model', model.d4t4.id)
    }
    if (query?.enquiryType) {
      setIsEnquiryQuery(true)
      methods.setValue('type_dropdown', query?.enquiryType)
      if (
        methods.getValues('type_dropdown') === 'new_car' ||
        methods.getValues('type_dropdown') === 'fleet_business'
      ) {
        setShowModel(true)
      }
    }
    if (query?.comment === 'whce') {
      methods.setValue('notes', 'Winter Health Checks Enquiry')
    }
  }, [query])

  const dropdownChanged = methods.watch('type_dropdown')

  useEffect(() => {
    if (query.enquiryType) {
      setTypeOfEnquiriesValues(
        brand === 'lexus'
          ? TYPE_OF_ENQUIRIES_LEXUS_VALUES
          : TYPE_OF_ENQUIRIES_TOYOTA_VALUES,
      )
      const typeOfEnquiry = methods.getValues('type_dropdown')
      const values = typeOfEnquiriesValues[typeOfEnquiry]
      if (values) {
        methods.setValue('type', values.type)
        methods.setValue('type_of_enquiry', values.type_of_enquiry)
        methods.setValue('enquiry_description', values.enquiry_description)
        methods.setValue('subject', values.subject)
        methods.setValue('pvt_company_car', values.pvt_company_car)
      }
    }
  }, [query, dropdownChanged])

  useEffect(() => {
    if (!query.enquiryType) {
      const typeOfEnquiry = methods.getValues('type_dropdown')

      if (typeOfEnquiry) {
        const values = typeOfEnquiriesValues[typeOfEnquiry]

        methods.setValue('type', values.type)
        methods.setValue('type_of_enquiry', values.type_of_enquiry)
        methods.setValue('enquiry_description', values.enquiry_description)
        methods.setValue('subject', values.subject)
        methods.setValue('pvt_company_car', values.pvt_company_car)
      }
    }
  }, [dropdownChanged])

  useEffect(() => {
    if (dealer) {
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )
      methods.setValue('dealer_name', dealer.name)
      methods.setValue('is_dealer', true)
      if (dealer?.applications?.NMSC?.externalID) setIsDealer(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealer])

  useEffect(() => {
    if (campaign.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }
    if (campaign.brochure) {
      methods.setValue('campaign_response_code', campaign.brochure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  const handleClick = (e) => {
    if (
      brand === 'lexus' &&
      (e.target.value === 'servicing' ||
        e.target.value === 'notlisted_current' ||
        e.target.value === 'parts' ||
        e.target.value === 'repair')
    ) {
      setShowReg(true)
    } else {
      setShowReg(false)
    }
    if (e.target.value === 'new_car' || e.target.value === 'fleet_business') {
      const modelSet = methods.getValues('model')
      if (!modelSet) {
        setShowModel(true)
      }
    } else {
      methods.resetField('model')
      setShowModel(false)
    }
  }

  const onSubmit = async (data) => {
    /* eslint-disable no-param-reassign */
    dealersList.forEach((dealerFound) => {
      if (dealerFound.localDealerID === data.dealer) {
        data.dealer_name = dealerFound.name
      }
    })
    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/contact-us-dealer`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: data?.type_of_enquiry || '',
          page: tracking.page,
        })
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: data?.type_of_enquiry || '',
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav title={content.title} />
      <FormWrapper>
        <FormStep subtitle={content.details.subtitle} active={true}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              {!isDealer && (
                <FindDealer
                  isError={
                    'dealer' in methods.formState.errors && !dealers.length
                  }
                  label={`${
                    brand === 'lexus' ? 'Choose' : 'Find'
                  } your preferred ${brand === 'lexus' ? 'centre' : 'dealer'}`}
                  infoText="Enter your postcode/location"
                  value={searchTerm}
                  search={(dealersFound, term) => {
                    setSerchTerm(term)
                    const dealersOptions = dealersFound.map((dealerObj) => ({
                      label: dealerObj?.name,
                      value: dealerObj?.localDealerID,
                      name: dealerObj?.name,
                      uuid: dealerObj?.uuid,
                      address: dealerObj?.address,
                    }))
                    setDealersList(dealersFound)
                    if (dealersOptions.length) {
                      const dealerList = removeDuplicate(
                        dealersOptions,
                        'value',
                      )
                      setDealers(dealerList)
                      methods.setValue('dealer', dealerList[0].value, {
                        shouldValidate: true,
                      })
                    }
                  }}
                />
              )}
              {dealers.length > 0 && !isDealer && (
                <ClosestDealers
                  label={`${
                    brand === 'lexus'
                      ? 'Please select a Lexus Centre'
                      : 'Nominated Dealers'
                  }`}
                  helper={`${
                    brand === 'lexus'
                      ? 'List displays the closest centre to your postcode.'
                      : 'List displays the closest dealer to your postcode.'
                  }`}
                  dealers={dealers}
                />
              )}
              <Title optional />
              <FirstName />
              <LastName />
              <Email />
              <Tel optional />
              {!isEnquiryQuery && (
                <DropDown
                  label={`${
                    brand === 'lexus'
                      ? 'Select Enquiry Type'
                      : 'What would you like to discuss?'
                  }`}
                  name="type_dropdown"
                  options={typeOfEnquiries}
                  onChange={handleClick}
                />
              )}
              {showReg && <VehicleRegNo optional />}
              {showModel && !isModelQuery && (
                <Models models={models} label="Model of interest" />
              )}
              <Comments
                optional
                name="notes"
                characterLimit={1500}
                label={`${
                  brand === 'lexus'
                    ? 'Enquiry details'
                    : 'Let us know if you have any specific requirements.'
                }`}
              />
              <MarketingConsent />
              <Button type="submit" submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'dealer'
  const content = await import(
    `../../content/${brand}/forms/contact-us/${form}.md`
  ).catch(() => null)
  const settings = await import(
    `../../content/${brand}/settings/global.md`
  ).catch(() => null)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      models: getModels(brand, form),
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: 'contact-dealer',
        form: 'lead-contact-dealer',
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

Dealer.whyDidYouRender = true

export default Dealer
